/* ManualSelector – two-step selector (manual → locale)        */
/* ------------------------------------------------------------------ */
import * as React from "react";
import PropTypes from "prop-types";
import { useLocation } from "@reach/router";
import { Link as RouterLink } from "react-router-dom";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";

/* Helper: group entries by manual-id */
function groupById(manuals) {
  const map = new Map(); // id → { title, variants[] }
  manuals.forEach(m => {
    if (!map.has(m.id)) map.set(m.id, { title: m.title, variants: [] });
    map.get(m.id).variants.push(m);
  });
  return map;
}

export default function ManualSelector({ manuals, prevPosition = null }) {
  const manualsMap = React.useMemo(() => groupById(manuals), [manuals]);
  const manualOptions = Array.from(manualsMap.entries()); // [id, {title,variants}]

  /* ---------- current query-string ---------- */
  const { search: existingQs } =
    typeof window !== "undefined" ? useLocation() : { search: "" }; // SSR-safe fallback

  /* ---------------- state */
  const [manualId, setManualId] = React.useState("");
  const [siteId, setSiteId] = React.useState("");

  /* Build href when both values exist */
  const basePath =
    manualId && siteId
      ? `/importexport/manual/${manualId}/site/${siteId}/`
      : "";

  // keep the leading “?” Gatsby/Reach gives us (empty string if none)
  const manualPath = basePath ? `${basePath}${existingQs}` : "";

  /* ---------------- handlers */
  function handleManualChange(e) {
    const id = e.target.value;
    setManualId(id);
    setSiteId(""); // reset locale until chosen
  }

  function handleSiteChange(e) {
    setSiteId(e.target.value);
  }

  /* ---------------- render */
  return (
    <Stack spacing={3} sx={{ maxWidth: 400, mx: "auto" }}>
      {/* manual dropdown */}
      <FormControl fullWidth size="small">
        <InputLabel id="manual-dd-label">Select Manual</InputLabel>
        <Select
          labelId="manual-dd-label"
          value={manualId}
          label="Select Manual"
          onChange={handleManualChange}
        >
          {manualOptions.map(([id, { title, userTitle }]) => (
            <MenuItem key={id} value={id}>
              {title} {`(${id})`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* locale dropdown – enabled only once a manual is chosen */}
      <FormControl fullWidth size="small" disabled={!manualId}>
        <InputLabel id="site-dd-label">Select Site / Locale</InputLabel>
        <Select
          labelId="site-dd-label"
          value={siteId}
          label="Select Site / Locale"
          onChange={handleSiteChange}
        >
          {manualId &&
            manualsMap.get(manualId).variants.map(v => (
              <MenuItem key={`${v.id}-${v.siteId}`} value={v.siteId}>
                {v.siteHandle
                  ? `${v.siteHandle} (${v.siteId} ${v.language}) ${
                      v.enabled ? "" : "(disabled)"
                    }`
                  : `Site ${v.siteId} ${v.language}) ${
                      v.enabled ? "" : "(disabled)"
                    }`}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* primary action */}
      <Button
        component={RouterLink}
        to={manualPath || undefined}
        variant="contained"
        disabled={!manualPath}
      >
        Go to Manual
      </Button>

      {/* optional back link */}
      {prevPosition && (
        <Button
          component={RouterLink}
          to={prevPosition}
          variant="outlined"
          color="secondary"
        >
          Go Back
        </Button>
      )}
    </Stack>
  );
}

ManualSelector.propTypes = {
  manuals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      siteId: PropTypes.string.isRequired,
      siteHandle: PropTypes.string,
      title: PropTypes.string,
      language: PropTypes.string,
    })
  ).isRequired,
  prevPosition: PropTypes.string,
};
